// Legacy function-call detectors used only for migration warnings.
// Example: `name(...)` and `@fun name(...)` should warn and not expand.
import { findMatchingPair } from '../../loops/for-loop/scanner-utils'

// Resolve legacy statement calls like `name(...)` for migration warnings.
export function parseLegacyCallName(statement: string): string | null {
  const trimmed = statement.trim()
  // Check whether the statement can still be a legacy bare call.
  if (!trimmed || trimmed.includes(':') || trimmed.includes('{') || trimmed.includes('}') || trimmed[0] === '@') {
    // Return null for empty text or non-call statement forms.
    return null
  } else {
    // Continue legacy call detection for bare identifiers.
  }

  let cursor = 0
  // Consume identifier and hyphen segments.
  while (cursor < trimmed.length && /[a-zA-Z0-9_-]/.test(trimmed[cursor])) {
    cursor += 1
  }
  // Check whether a valid legacy function name prefix was parsed.
  if (cursor === 0) {
    // Return null when no valid function name prefix exists.
    return null
  } else {
    // Continue with parsed legacy call name.
  }

  const name = trimmed.slice(0, cursor)
  const argsStart = skipWhitespace(trimmed, cursor)
  // Check whether the parsed legacy name is followed by `(`.
  if (trimmed[argsStart] !== '(') {
    // Return null when legacy call has no opening parenthesis.
    return null
  } else {
    // Continue argument shape validation.
  }

  // Return the parsed function name only for full-statement legacy calls.
  return findMatchingPair(trimmed, argsStart, '(', ')') === trimmed.length - 1 ? name : null
}

// Resolve legacy `@fun name(...)` calls for migration warnings.
export function parseLegacyPrefixedCallName(statement: string): string | null {
  const trimmed = statement.trim()
  // Check whether the statement still matches legacy prefixed syntax.
  if (!trimmed.startsWith('@fun') || trimmed.startsWith('@fun.') || trimmed.includes('{') || trimmed.includes('}')) {
    // Return null when current statement does not use legacy prefixed syntax.
    return null
  } else {
    // Continue detection for whitespace-prefixed @fun call syntax.
  }

  let cursor = 4
  // Check whether `@fun` is followed by required whitespace.
  if (!/\s/.test(trimmed[cursor] ?? '')) {
    // Return null when `@fun` is not followed by required whitespace.
    return null
  } else {
    // Continue scanning after required whitespace separator.
  }

  cursor = skipWhitespace(trimmed, cursor)
  const nameStart = cursor
  // Consume identifier and hyphen segments.
  while (cursor < trimmed.length && /[a-zA-Z0-9_-]/.test(trimmed[cursor])) {
    cursor += 1
  }
  // Check whether a valid function name exists after `@fun`.
  if (cursor <= nameStart) {
    // Return null when no valid function name exists after `@fun`.
    return null
  } else {
    // Continue parsing argument list for legacy prefixed call.
  }

  const name = trimmed.slice(nameStart, cursor)
  const argsStart = skipWhitespace(trimmed, cursor)
  // Check whether the parsed name is followed by `(`.
  if (trimmed[argsStart] !== '(') {
    // Return null when parsed name is not followed by `(`.
    return null
  } else {
    // Continue validating parenthesis range.
  }

  // Return the parsed legacy name only for full-statement calls.
  return findMatchingPair(trimmed, argsStart, '(', ')') === trimmed.length - 1 ? name : null
}

// Resolve the next non-whitespace index while scanning function-call text.
function skipWhitespace(source: string, start: number): number {
  let cursor = start
  // Advance until the next non-whitespace character appears.
  while (cursor < source.length && /\s/.test(source[cursor])) {
    cursor += 1
  }
  // Return the first non-whitespace index.
  return cursor
}
